import { createClassNames, createStylesheet } from "../utils/styles";
import { UIBox, UITextField, UITextFieldProps } from ".";
import {
  ChangeEvent,
  ClipboardEvent,
  createRef,
  FocusEvent,
  Fragment,
  KeyboardEvent,
  RefObject,
  useCallback,
  useEffect,
  useState,
} from "react";
import { Modify } from "../utils/types";

type Props = Modify<
  UITextFieldProps,
  {
    containerProps?: React.ComponentProps<typeof UIBox>;
    length?: number;
    splitInterval?: number;
    renderSplitComponent?: () => React.ReactNode;
    defaultValue?: string;
    onChange?: (chars: CharacterItem[]) => void;
    onCompleted?: (code: string) => void;
    error?: boolean;
    success?: boolean;
  }
>;

type CharacterItem = {
  key: number;
  value: string;
  ref: RefObject<HTMLInputElement>;
};

export const UIInputOtp = ({
  length = 6,
  splitInterval = 3,
  renderSplitComponent,
  error,
  success,
  containerProps,
  onChange,
  onCompleted,
  defaultValue,
  ...props
}: Props) => {
  const classes = useStyles();

  const sanitizedValue = useCallback((value?: string) => {
    return value?.replace(/\D/g, "").toUpperCase() ?? "";
  }, []);

  const [chars, setChars] = useState<CharacterItem[]>(
    new Array(length).fill(undefined).map((_, index) => ({
      key: index,
      value: sanitizedValue(defaultValue?.charAt(index)),
      ref: createRef() as RefObject<HTMLInputElement>,
    })),
  );

  useEffect(() => {
    onChange?.(chars);
    const isComplete = chars.every((item) => item.value);
    if (isComplete) {
      const code = sanitizedValue(chars.map((item) => item.value).join(""));
      onCompleted?.(code);
    }
  }, [chars, onChange, onCompleted, sanitizedValue]);

  const handleInputChange = useCallback(
    (itemKey: number) => (event: ChangeEvent<HTMLInputElement>) => {
      const val = event.target.value?.charAt(0);
      const sanitizedVal = sanitizedValue(val);
      setChars((prev) => {
        const newChars = [...prev];
        newChars[itemKey].value = sanitizedVal;
        return newChars;
      });
      if (sanitizedVal && chars[itemKey + 1]) {
        chars[itemKey + 1]?.ref?.current?.focus();
      }
    },
    [sanitizedValue, chars],
  );

  const handleKeyDown = useCallback(
    (itemKey: number, itemValue: string) => (event: KeyboardEvent<HTMLInputElement>) => {
      if (event.key === "Backspace") {
        if (itemValue) {
          setChars((prev) => {
            const newChars = [...prev];
            newChars[itemKey].value = "";
            return newChars;
          });
        } else if (itemKey > 0) {
          chars[itemKey - 1]?.ref?.current?.focus();
          setChars((prev) => {
            const newChars = [...prev];
            newChars[itemKey - 1].value = "";
            return newChars;
          });
        }
      } else if (event.key === "ArrowLeft" && itemKey > 0) {
        event.preventDefault();
        chars[itemKey - 1]?.ref?.current?.focus();
      } else if (event.key === "ArrowRight" && itemKey < chars.length - 1) {
        event.preventDefault();
        chars[itemKey + 1]?.ref?.current?.focus();
      }
    },
    [chars],
  );

  const handlePasteCapture = useCallback(
    (event: ClipboardEvent<HTMLInputElement>) => {
      const pastedData = event.clipboardData.getData("text/plain").replace(/\D/g, "").toUpperCase();
      if (pastedData.length >= length) {
        setChars((prev) =>
          prev.map((char, index) => ({
            ...char,
            value: sanitizedValue(pastedData.charAt(index)),
          })),
        );
      }
    },
    [length, sanitizedValue],
  );

  const handleFocus = useCallback((event: FocusEvent<HTMLInputElement>) => {
    event.target.select();
  }, []);

  const renderSplitCharacter = useCallback(() => {
    if (renderSplitComponent) return renderSplitComponent();
    const className = createClassNames([classes.splitCharacter, success && "success", error && "error"]);
    return <div className={className} />;
  }, [renderSplitComponent, classes.splitCharacter, success, error]);

  return (
    <UIBox {...containerProps} className={`${classes.ctr} ${containerProps?.className}`}>
      {chars.map((item) => {
        const isAtSplitInterval = Boolean((item.key + 1) % splitInterval === 0);
        const isLastItem = item.key === chars.length - 1;
        return (
          <Fragment key={item.key}>
            <UITextField
              inputRef={item.ref}
              name={`otp-${item.key}`}
              type="text"
              variant="outlined"
              inputMode="numeric"
              {...props}
              className={createClassNames([
                classes.input,
                item.value && classes.filled,
                error && classes.error,
                success && classes.success,
                props.className,
              ])}
              value={item.value}
              onFocus={handleFocus}
              onChange={handleInputChange(item.key)}
              onKeyDown={handleKeyDown(item.key, item.value)}
              onPasteCapture={handlePasteCapture}
            />
            {isAtSplitInterval && !isLastItem && renderSplitCharacter()}
          </Fragment>
        );
      })}
    </UIBox>
  );
};

const useStyles = createStylesheet((theme) => ({
  ctr: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(0.5),
    width: "100%",
    [theme.breakpoints.up("sm")]: {
      gap: theme.spacing(2),
    },
  },
  input: {
    minWidth: 0,
    flex: 1,
    width: "auto",
    maxWidth: "2.5rem",
    [theme.breakpoints.up("sm")]: {
      maxWidth: "4rem",
    },
    "& > div": {
      aspectRatio: "1/1",
      minHeight: "2.5rem",
      width: "100%",
    },
    "& input": {
      textAlign: "center",
      fontSize: "1rem",
      fontWeight: 600,
      padding: theme.spacing(0.5),
      [theme.breakpoints.up("sm")]: {
        fontSize: theme.typography.h3.fontSize,
        padding: theme.spacing(1.5),
      },
    },
    "& fieldset": {
      borderWidth: 2,
      borderStyle: "solid",
      borderColor: theme.palette.grey[200],
    },
  },
  filled: {
    "& > div > fieldset": {
      borderColor: theme.palette.grey[400],
    },
  },
  error: {
    "& > div > fieldset": {
      borderColor: theme.palette.error.main + " !important",
    },
  },
  success: {
    "& > div > fieldset": {
      borderColor: theme.palette.success.main + " !important",
    },
  },
  splitCharacter: {
    width: "0.5rem",
    height: 2,
    backgroundColor: theme.palette.grey[300],
    flexShrink: 0,
    [theme.breakpoints.up("sm")]: {
      width: "2rem",
    },
    "&.error": {
      backgroundColor: theme.palette.error.main,
    },
    "&.success": {
      backgroundColor: theme.palette.success.main,
    },
  },
}));

export default UIInputOtp;
